import { Controller } from "react-hook-form";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { DeletableChips } from "../DeletableChips/DeletableChips";
import { RadioButton } from "../RadioButton/RadioButton";
import { SearchSelectInfiniteScroll } from "../SearchSelectInfiniteScroll/SearchSelectInfiniteScroll";
import { SelectBoxV2 } from "../SelectBoxV2/SelectBoxV2";
import { TextField } from "../TextFields/TextFields";
import { H6Bold } from "../Typography/Typography";
import { RadioButtonContainer } from "../../layout/FormLayout";
import type { OptionType } from "../../types/types";
import { endpoints } from "../../endpoints";
import type { AttributeSchema } from "../../types/types.PIM";
import {
  getPreviewAttributeLabel,
  getRequiredPreviewAttributeName,
} from "./utils";
import type { ObjectReferenceProps } from "./objectReference.types";
import { useMdmEntityDisplayNames } from "../../util/mdmEntityDisplayNames";

const TwoColumnRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${({ theme }) => theme.resolve("spacing_xl")};
`;

const PreviewTitle = styled(H6Bold)`
  margin-bottom: ${({ theme }) => theme.resolve("spacing_lg")};
`;

const mapAttributeOptions = (
  response: AttributeSchema[] | { data?: AttributeSchema[] }
): OptionType<string>[] => {
  const attributes = Array.isArray(response) ? response : response?.data ?? [];
  return attributes.map((attribute) => ({
    label: getPreviewAttributeLabel(attribute),
    value: attribute.id,
  }));
};

/** only consider form_field for preview attributes */
const previewAttributeParams = new URLSearchParams([
  ["attribute_type", "form_field"],
]);

export const ObjectReference = ({
  objectReferenceType,
  onObjectReferenceTypeChange,
  tenantId,
  control,
  formState,
  errors,
  register,
  addSourcePreviewAttribute,
  addTargetPreviewAttribute,
  removeSourcePreviewAttribute,
  removeTargetPreviewAttribute,
  sourcePreviewChips,
  targetPreviewChips,
  sourceObjectType,
  targetObjectType,
  disableObjectTypeSelection = false,
  disableReferenceTypeSelection = false,
}: ObjectReferenceProps) => {
  const { t } = useTranslation();
  const { getLabel } = useMdmEntityDisplayNames();

  const objectTypeOptions: OptionType<string>[] = [
    { label: getLabel("supplier", "plural"), value: "supplier" },
    { label: getLabel("customer", "plural"), value: "customer" },
    { label: getLabel("distributor", "plural"), value: "distributor" },
    { label: t("Products"), value: "product" },
  ];

  const requiredSourceName = getRequiredPreviewAttributeName(sourceObjectType);
  const requiredTargetName = getRequiredPreviewAttributeName(targetObjectType);

  const isRequiredChip = (chipName: string, requiredName?: string): boolean => {
    if (!requiredName) return false;
    return chipName.startsWith(requiredName);
  };

  const sourceChipsWithStatus = sourcePreviewChips.map((chip) => ({
    ...chip,
    active:
      chip.active !== false && !isRequiredChip(chip.name, requiredSourceName),
  }));

  const targetChipsWithStatus = targetPreviewChips.map((chip) => ({
    ...chip,
    active:
      chip.active !== false && !isRequiredChip(chip.name, requiredTargetName),
  }));

  const removeSource = (name: string) => {
    if (!isRequiredChip(name, requiredSourceName)) {
      removeSourcePreviewAttribute(name);
    }
  };

  const removeTarget = (name: string) => {
    if (!isRequiredChip(name, requiredTargetName)) {
      removeTargetPreviewAttribute(name);
    }
  };

  return (
    <>
      <TwoColumnRow>
        <Controller
          as={SelectBoxV2}
          control={control}
          name="source_object_type"
          autoComplete="source_object_type"
          placeholder={t("Source Object Type")}
          options={objectTypeOptions.map((option) => ({
            label: option.label,
            value: option.value,
          }))}
          rules={{ required: true }}
          errors={errors}
          formState={formState}
          isDisabled={disableObjectTypeSelection}
        />
        <Controller
          as={SelectBoxV2}
          control={control}
          name="target_object_type"
          autoComplete="target_object_type"
          placeholder={t("Target Object Type")}
          options={objectTypeOptions.map((option) => ({
            label: option.label,
            value: option.value,
          }))}
          rules={{ required: true }}
          errors={errors}
          formState={formState}
          isDisabled={disableObjectTypeSelection}
        />
      </TwoColumnRow>
      <RadioButtonContainer>
        <RadioButton
          name="object_reference_type"
          value="single"
          checked={objectReferenceType === "single"}
          optionTitle={t("Single Reference")}
          handleChange={onObjectReferenceTypeChange}
          disabled={disableReferenceTypeSelection}
        />
        <RadioButton
          name="object_reference_type"
          value="multi"
          checked={objectReferenceType === "multi"}
          optionTitle={t("Multi Reference")}
          handleChange={onObjectReferenceTypeChange}
          disabled={disableReferenceTypeSelection}
        />
      </RadioButtonContainer>
      <TextField
        name="target_side_display_name"
        label={t("Target Side Display Name")}
        theref={register({
          required: false,
        })}
        formState={formState}
        errors={errors}
        type="text"
      />
      <PreviewTitle>{t("Source Object Preview Attributes")}</PreviewTitle>
      <SearchSelectInfiniteScroll
        name="source_preview_attributes"
        baseUrl={endpoints.v2_tenants_id_pim_attributes(tenantId)}
        params={previewAttributeParams}
        getOptions={mapAttributeOptions}
        onChange={addSourcePreviewAttribute}
        errors={errors}
        formState={formState}
        placeholder={t("Select Attribute")}
        value={null}
        autoComplete="on"
      />
      <DeletableChips
        chips={sourceChipsWithStatus}
        noneSelectedName="source_preview_attributes"
        handleClick={removeSource}
        showNoneSelectedMessage={false}
      />
      <PreviewTitle>{t("Target Object Preview Attributes")}</PreviewTitle>
      <SearchSelectInfiniteScroll
        name="target_preview_attributes"
        baseUrl={endpoints.v2_tenants_id_pim_attributes(tenantId)}
        params={previewAttributeParams}
        getOptions={mapAttributeOptions}
        onChange={addTargetPreviewAttribute}
        errors={errors}
        formState={formState}
        placeholder={t("Select Attribute")}
        value={null}
        autoComplete="on"
      />
      <DeletableChips
        chips={targetChipsWithStatus}
        noneSelectedName="target_preview_attributes"
        handleClick={removeTarget}
        showNoneSelectedMessage={false}
      />
    </>
  );
};
